import React from 'react';
import style from './style.module.scss';
import { observer } from 'mobx-react';
import { Avatar, Select, Spin, Table } from 'antd';
import { useAuthStore, useNhlGamesStore } from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { LeaderboardSearchParams } from '../../stores/nhl-game';
import moment from 'moment';
import { ImageHolder } from '../../components/image-holder';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { ColumnProps } from 'antd/lib/table';
import { Leaderboard, NhlGameLeaderboard } from '../../types/nhl-game';
import * as Colors from '../../styles/colors';
import { Text } from '../../utils/text';

/* TODO/pagination: not sure why but it seems harder on this page to get the pagination TOTAL, so we need to rely on little tricks... This should be fixed at the modeling/api level instead. */
const getPaginationTotal = (
    leaderboard: NhlGameLeaderboard | null,
    currentPage: number
): number | null => {
    if (!leaderboard || !leaderboard.result) {
        return null;
    }

    // If API provides total, use it
    if (
        leaderboard.total !== undefined &&
        typeof leaderboard.total === 'number'
    ) {
        return leaderboard.total;
    }

    // Otherwise, calculate from current page and results
    const perPage = leaderboard.perPage;
    const resultCount = leaderboard.result.length;

    // If we have a full page, estimate minimum total
    // Otherwise, this is the last page, so we know the exact total
    return resultCount >= perPage
        ? (currentPage + 1) * perPage + 1 // At least one more item
        : currentPage * perPage + resultCount; // Exact total on last page
};

export const NhlGamesLeaderboardPage = observer(() => {
    const store = useNhlGamesStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const columns = React.useMemo<ColumnProps<Leaderboard>[]>(
        () => [
            {
                title: Text.nhlGame.leaderBoardContainer.table.id,
                dataIndex: 'userId',
                key: 'id',
                width: '100px',
                ellipsis: true,
                render: (_: unknown, record: Leaderboard) => (
                    <span>{record.userId}</span>
                ),
            },
            {
                title: Text.nhlGame.leaderBoardContainer.table.name,
                key: 'name',
                ellipsis: true,
                render: (_: unknown, record: Leaderboard) => (
                    <>
                        <Avatar
                            style={{
                                verticalAlign: 'middle',
                                backgroundColor: Colors.RED_500,
                            }}
                            size={30}
                        >
                            {record.firstName?.charAt(0).toLocaleUpperCase()}
                            {record.lastName?.charAt(0).toLocaleUpperCase()}
                        </Avatar>

                        <span className={style.name}>
                            {record.firstName} {record.lastName}
                        </span>
                    </>
                ),
            },
            {
                title: Text.nhlGame.leaderBoardContainer.table.email,
                dataIndex: 'email',
                key: 'email',
                ellipsis: true,
                render: (_: unknown, record: Leaderboard) => (
                    <span>{record.email}</span>
                ),
            },
            {
                title: Text.nhlGame.leaderBoardContainer.table.totalPoints,
                key: 'totalPoints',
                ellipsis: true,
                render: (_, record: Leaderboard) => (
                    <span>{record.totalPoints}</span>
                ),
            },
            {
                title: Text.nhlGame.leaderBoardContainer.table.registration,
                key: 'registration',
                ellipsis: true,
                render: (_, record: Leaderboard) => (
                    <span>{moment(record.createdAt).format('LLL')}</span>
                ),
            },
        ],
        []
    );

    const search = useSearchParams<LeaderboardSearchParams>();

    React.useEffect(() => {
        store.getValidNhlGames();
    }, [store]);

    React.useEffect(() => {
        store.getNhlGameLeaderboard(search.params);
    }, [store, search.params]);

    const total = getPaginationTotal(
        store.leaderboard,
        search.params.page ?? 0
    );

    return (
        <>
            <PageTopBar title={Text.nhlGame.leaderBoardContainer.pageTitle} />

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        <Select
                            loading={store.isFetchingValidList}
                            onChange={(v) => {
                                search.setParams({
                                    nhlGameId: Number(v),
                                    page: 0,
                                });
                            }}
                            className={style.config_field}
                            optionLabelProp="label"
                        >
                            {store.leaderboardList?.results.map((game) => (
                                <Select.Option
                                    key={game.id}
                                    value={game.id}
                                    label={`#${game.id} ${game.title.en}`}
                                >
                                    <div className={style.custom_option}>
                                        <div>#{game.id}</div>
                                        <div className={style.titles_holder}>
                                            <span>{game.title.en}</span>
                                            <span>{game.title.fr}</span>
                                        </div>
                                        <ImageHolder
                                            src={game.image.en}
                                            size={40}
                                        />
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>

                {total !== null && (
                    <PaginationBar
                        pagination={{
                            page: search.params.page,
                            pageSize: search.params.perPage,
                            total,
                            onPageChange: (page) => search.setParams({ page }),
                            onPageSizeChange: (perPage) =>
                                search.setParams({ perPage, page: 0 }),
                        }}
                    />
                )}
            </div>

            <div>
                <div className={style.input_row}>
                    {store.isFetchingLeaderboard ? (
                        <Spin />
                    ) : store.leaderboard?.result ? (
                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={store.leaderboard?.result || []}
                            loading={store.isFetchingLeaderboard}
                            bordered
                            pagination={false}
                        ></Table>
                    ) : (
                        <div className={style.no_game_selected}>
                            <p>
                                {
                                    Text.nhlGame.leaderBoardContainer
                                        .noGameSelected
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
});
